import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import {
  Botao,
  BotaoStep,
  Checkbox,
  IconeCartaoAmex,
  IconeCartaoMaster,
  IconeCartaoVisa,
  ModalRef,
  PdfViewer,
  FormUnform,
  FormRef,
  InputUnform,
  SelectUnform,
  WizardContainer,
  FuncoesMoeda,
  InputMesAnoUnform,
  IconeInformacao,
  Tooltip,
  Cores,
  scrollParaTopo,
  CartaoCreditoInputUnform,
  focarPrimeiroCampoComErro,
  IconeCartaoElo,
  IconeCartaoHiper,
  IconeCartaoJcb,
  IconeCartaoDiners,
  regExpDiners,
  regExpJcb,
  regExpHiper,
  regExpElo,
  regExpAmex,
  regExpMaster,
  regExpVisa,
  SelectOpcao
} from 'src/componentes'
import {
  SELECT_DIAS_DIAS_BOLETO_AUTOMATICO,
  SELECT_METODOSPAGAMENTO
} from 'src/dados-estaticos'
import { Api } from 'src/servicos'
import { enviarEventoGtm } from 'src/servicos/analytics'
import { BoletoGerado } from 'src/servicos/api/tipos'
import { CupomValido, MetodosPagamento, PagamentoMatricula } from 'src/tipos'
import { v4 } from 'uuid'

import ReingressoContainer from '../container'
import {
  schemaPagamentoBoleto,
  schemaPagamentoCartaoCredito,
  schemaPagamentoCartaoRecorrencia,
  schemaPagamentoPix
} from '../schema'
import {
  ContainerConteudo,
  ContainerAcoes,
  ContainerCadastro,
  ContainerCupom,
  Linha,
  ContainerMedio,
  ContainerInputCupom,
  TituloIsento,
  TituloIsentoRecorrencia,
  LinhaBandeiras,
  ContainerBandeiras,
  ContainerBotaoBoleto,
  ContainerBotaoPix,
  ContainerBotaoAtualizar,
  LinhaPagamentoPix,
  ContainerBoleto,
  IconeLabel
} from '../style'
import { InfoValoresCurso } from './info-valores-curso'
import { ModalPagamentoReingresso } from './modal-pagamento-reingresso'
import { CupomContainer } from './styles'

export const PagamentoReingresso: React.FC = () => {
  const { id } = useParams<{ id?: string }>()
  const modalRef = useRef<ModalRef>(null)
  const formRef = useRef<FormRef>(null)
  const formCupomRef = useRef<FormRef>(null)

  const { dados, definirPagamento } = ReingressoContainer.useContainer()
  const { voltarWizard, passo } = WizardContainer.useContainer()

  const [carregandoBoleto, definirCarregandoBoleto] = useState(false)
  const [carregandoFinalizar, definirCarregandoFinalizar] = useState(false)
  const [carregandoVoltar, definirCarregandoVoltar] = useState(false)

  const [metodoPagamento, definirMetedoPagamento] = useState(
    SELECT_METODOSPAGAMENTO[0].id
  )
  const [boleto, definirBoleto] = useState<undefined | BoletoGerado>(undefined)
  const [pixCopiaECola, definirPixCopiaECola] = useState('')
  const [pdfKey, definirPdfKey] = useState(0)
  const [numeroCartao, definirNumeroCartao] = useState('')
  const [temCupom, definirTemCupom] = useState(false)
  const [cupom, definirCupom] = useState<CupomValido>({} as CupomValido)
  const [codigoCupom, definirCodigoCupom] = useState<string>(null)

  const [ehPagamentoRecorrente, definirEhPagamentoRecorrente] = useState(false)

  const obterDados = async () => {
    try {
      const dados = await Api.RequisitarInformacoesTaxaMatriculaReingresso(id)
      definirEhPagamentoRecorrente(dados.pagamentoRecorrente)
    } catch (error) {}
  }

  const valorDesconto = useMemo(() => {
    return cupom?.valorDesconto ?? 0
  }, [cupom])

  const valorTotal = useMemo(() => {
    if (ehPagamentoRecorrente) {
      return 0
    }

    const valorTaxa = dados?.valorTaxaReingresso ?? 0
    if (valorDesconto < valorTaxa) {
      return valorTaxa - valorDesconto
    }

    return 0
  }, [dados?.valorTaxaReingresso, ehPagamentoRecorrente, valorDesconto])

  const parcelasCartaoCredito = useMemo(() => {
    const quantidade = Array.from(
      { length: dados?.quantidadeMaximaParcelas },
      (x, i) => {
        const contador = `${i + 1}`
        return {
          id: contador,
          texto: `${contador}x de ${FuncoesMoeda.formatarMoeda(
            valorTotal / +contador
          )}`
        } as SelectOpcao
      }
    )

    return quantidade
  }, [valorTotal, dados])

  const acaoSucesso = async (dadosPagamento: PagamentoMatricula) => {
    if (metodoPagamento !== SELECT_METODOSPAGAMENTO[0].id) {
      try {
        definirCarregandoBoleto(true)
        const pix =
          metodoPagamento === SELECT_METODOSPAGAMENTO[2].id
            ? 'pix'
            : 'pix-e-boleto'
        const boletoGerado = await Api.GerarBoletoMatriculaReingresso(
          id,
          cupom?.cupomId,
          pix
        )

        definirBoleto(boletoGerado)
        const pagamento = {
          ...dadosPagamento,
          boletoGerado,
          valorTotal: valorTotal,
          valor: dados?.valorTaxaReingresso,
          idCupom: cupom?.cupomId
        }
        definirPagamento(pagamento)
      } finally {
        definirCarregandoBoleto(false)
      }
    } else {
      try {
        definirCarregandoFinalizar(true)
        const dadosCartao = {
          nomeImpresso: dadosPagamento.nomeCartao,
          numero: dadosPagamento.numeroCartao.replaceAll(' ', ''),
          validade: dadosPagamento.validadeCartao,
          codigo: dadosPagamento.codigoSeguranca,
          parcelas: Number(dadosPagamento.parcelas ?? 1)
        }

        await Api.AvancarPagamentoMatriculaReingresso(
          id,
          dadosCartao,
          cupom?.cupomId
        )
        modalRef?.current?.abrir()

        const pagamento = {
          ...dadosPagamento,
          valorTotal: valorTotal,
          valor: dados?.valorTaxaReingresso,
          idCupom: cupom?.cupomId
        }
        definirPagamento(pagamento)
      } finally {
        definirCarregandoFinalizar(false)
      }
    }

    const cursosGTM = []
    const cursoGTM = {
      id: v4(),
      name: dados?.matriculaDestino?.nomeCurso,
      category: dados?.matriculaDestino?.modalidade,
      price: dados?.valorTaxaReingresso,
      position: cursosGTM?.length + 1,
      metric1: 0,
      metric2: valorTotal,
      metric3: dadosPagamento.parcelas,
      metric4: dadosPagamento.diaVencimento
    }

    cursosGTM.push(cursoGTM)

    enviarEventoGtm({
      event: 'purchase',
      ecommerce: {
        purchase: {
          actionField: {
            id: v4(),
            option: dadosPagamento.metodoPagamento,
            step: 'Pagamento do Curso - Taxa de Reingresso',
            revenue: valorTotal
          },
          products: cursosGTM
        }
      }
    })
  }

  const acaoFinalizar = async () => {
    try {
      if (
        metodoPagamento === SELECT_METODOSPAGAMENTO[1].id ||
        metodoPagamento === SELECT_METODOSPAGAMENTO[2].id ||
        (valorTotal < 1 && !ehPagamentoRecorrente)
      ) {
        definirCarregandoFinalizar(true)
        await Api.AvancarPagamentoMatriculaReingresso(id, undefined, cupom?.cupomId)

        modalRef?.current?.abrir()
      } else {
        formRef.current?.submitForm()
      }
    } finally {
      definirCarregandoFinalizar(false)
    }
  }

  const validarCupom = async ({ codigoCupom }: { codigoCupom?: string }) => {
    if (codigoCupom) {
      const novoCupom = await Api.RequisitarValidarCupomTaxaReingresso(
        id,
        codigoCupom,
        metodoPagamento as MetodosPagamento
      )

      if (novoCupom) {
        definirCupom({ ...novoCupom, codigoCupom })
        formCupomRef.current?.setFieldError('codigoCupom', 'Cupom válido')
      } else {
        definirCupom({} as CupomValido)
        formCupomRef.current?.setFieldError('codigoCupom', 'Cupom inválido')
      }
    } else {
      definirCupom({} as CupomValido)
      formCupomRef.current?.setFieldError('codigoCupom', '')
    }
  }

  const getBoletoUrl = (url: string) => {
    const sliceCounter =
      url.indexOf('?formato=') > 0 ? url.indexOf('?formato=') : url.length

    if (metodoPagamento === SELECT_METODOSPAGAMENTO[1].id) {
      return url.slice(0, sliceCounter)
    }

    if (metodoPagamento === SELECT_METODOSPAGAMENTO[2].id) {
      return `${url.slice(0, sliceCounter)}?formato=pix`
    }
  }

  const recarregarPdf = () => {
    definirPdfKey(prev => prev + 1)
  }

  const renderizarBoleto = useCallback(
    () => {
      if (!boleto) {
        return <></>
      }

      return (
        <>
          <Linha>
            <ContainerBoleto>
              {(metodoPagamento === SELECT_METODOSPAGAMENTO[1].id ||
                metodoPagamento === SELECT_METODOSPAGAMENTO[2].id) && (
                <>
                  {metodoPagamento === SELECT_METODOSPAGAMENTO[1].id && (
                    <>
                      <h5>
                        Boleto gerado com sucesso!
                        <br />
                        Realize o pagamento do boleto em qualquer banco até a
                        data de vencimento
                      </h5>
                      <p>{boleto.linhaDigitavel}</p>
                      <Botao
                        tema="Link"
                        texto="Copiar Código do Boleto"
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(boleto.linhaDigitavel)
                        }}
                      />
                    </>
                  )}
                  {metodoPagamento === SELECT_METODOSPAGAMENTO[2].id && (
                    <>
                      {!pixCopiaECola ? (
                        <>
                          <h5>
                            PIX gerado com sucesso!
                            <br />
                            Realize o pagamento via Pix em qualquer banco em
                            até 24 horas
                          </h5>
                          <p>
                            Se o código Copia e Cola ou o QR Code não
                            apareceram para você, espere alguns instantes e
                            clique em atualizar!
                          </p>
                        </>
                      ) : (
                        <>
                          <h5>
                            PIX gerado com sucesso!
                            <br />
                            Realize o pagamento via Pix em qualquer banco em
                            até 24 horas
                          </h5>
                          <p>{pixCopiaECola}</p>
                          <Botao
                            tema="Link"
                            texto="Copiar Código do PIX"
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(pixCopiaECola)
                            }}
                          />
                        </>
                      )}
                    </>
                  )}
                </>
              )}
              {!!boleto.linkBoleto && (
                <PdfViewer
                  key={pdfKey}
                  url={getBoletoUrl(boleto.linkBoleto)}
                />
              )}
            </ContainerBoleto>
          </Linha>
          {metodoPagamento === SELECT_METODOSPAGAMENTO[2].id && (
            <ContainerBotaoAtualizar>
              <Botao
                texto="Atualizar"
                tema="Primario"
                type="button"
                onClick={recarregarPdf}
              />
            </ContainerBotaoAtualizar>
          )}
        </>
      )
    },
    [metodoPagamento, boleto, pixCopiaECola, pdfKey]
  )

  const tituloPagamento = useMemo(() => {
    if (ehPagamentoRecorrente) {
      return 'Cartão Pagamento Recorrente'
    }

    return 'Pagamento do Reingresso'
  }, [ehPagamentoRecorrente])

  const schema = useMemo(() => {
    if (ehPagamentoRecorrente) {
      return schemaPagamentoCartaoRecorrencia
    }

    return metodoPagamento === SELECT_METODOSPAGAMENTO[0].id
      ? schemaPagamentoCartaoCredito
      : metodoPagamento === SELECT_METODOSPAGAMENTO[2].id
      ? schemaPagamentoPix
      : schemaPagamentoBoleto
  }, [ehPagamentoRecorrente, metodoPagamento])

  const exibirCamposCartao = useMemo(() => {
    return valorTotal > 0 || ehPagamentoRecorrente
  }, [valorTotal, ehPagamentoRecorrente])

  const textoIsencao = useMemo(() => {
    return ehPagamentoRecorrente
      ? 'ISENTO: Não há parcelas do Plano de Pagamento a pagar.'
      : ' ISENTO: Não há valores de reingresso a pagar.'
  }, [ehPagamentoRecorrente])

  const ehMetodoPix = metodoPagamento === SELECT_METODOSPAGAMENTO[2].id

  const alterarMetodoPagamento = useCallback((valor: SelectOpcao | null) => {
    if (!valor) return

    definirMetedoPagamento(valor.id)
    definirNumeroCartao('')
    definirBoleto(undefined)
    definirPixCopiaECola('')
    formRef.current?.setErrors({})
  }, [])

  useEffect(() => {
    scrollParaTopo()
  }, [passo])

  useEffect(() => {
    if (metodoPagamento === SELECT_METODOSPAGAMENTO[1].id) {
      formRef.current?.setFieldValue(
        'diaVencimento',
        new Date(new Date().setDate(new Date().getDate() + 2))
          .getDate()
          .toString()
      )
    }
  }, [metodoPagamento])

  useEffect(() => {
    if (
      boleto &&
      boleto.boletoId &&
      metodoPagamento === SELECT_METODOSPAGAMENTO[2].id
    ) {
      const pegarQrCode = async () => {
        if (boleto.boletoId !== undefined) {
          const resposta = await Api.pegarQrCodeBoleto(boleto.boletoId)
          definirPixCopiaECola(resposta.qrCode)
        }
      }

      pegarQrCode()
    }
  }, [boleto, metodoPagamento])

  useEffect(() => {
    if (pixCopiaECola) {
      definirBoleto(prevBoleto => ({
        ...prevBoleto,
        pixCopiaECola
      }))
    }
  }, [pixCopiaECola])

  useEffect(() => {
    validarCupom({ codigoCupom })
  }, [metodoPagamento])

  useEffect(() => {
    obterDados()
  }, [id])

  return (
    <>
      <InfoValoresCurso
        key={dados?.id}
        valorTaxa={dados?.valorTaxaReingresso ?? 0}
        recorrencia={ehPagamentoRecorrente}
        valorDesconto={valorDesconto}
        valorTotal={valorTotal}
      />

      {!ehPagamentoRecorrente && (dados?.valorTaxaReingresso ?? 0) > 0 && (
        <ContainerConteudo>
          <ContainerCupom>
            <Checkbox
              id={`chk_cupom_reingresso_${dados?.id}`}
              texto="Tenho um Cupom"
              htmlFor={`chk_tem_cupom_reingresso_${dados?.id}`}
              tamanho="L"
            >
              <input
                type="checkbox"
                id={`chk_tem_cupom_reingresso_${dados?.id}`}
                checked={temCupom}
                disabled={
                  metodoPagamento !== SELECT_METODOSPAGAMENTO[0].id && !!boleto
                }
                onChange={() => {
                  definirTemCupom(old => !old)
                  definirCupom({} as CupomValido)
                }}
              />
            </Checkbox>

            {temCupom ? (
              <ContainerInputCupom>
                <FormUnform ref={formCupomRef} acaoSucesso={validarCupom}>
                  <CupomContainer>
                    <InputUnform
                      id="ipt_cupom_reingresso"
                      name="codigoCupom"
                      sucesso={!!cupom.cupomId}
                      disabled={
                        metodoPagamento !== SELECT_METODOSPAGAMENTO[0].id &&
                        !!boleto
                      }
                      onBlur={event => {
                        definirCodigoCupom(event.target.value)
                        if (
                          metodoPagamento !== SELECT_METODOSPAGAMENTO[0].id &&
                          !!boleto
                        ) {
                          return
                        }
                        formCupomRef.current?.submitForm()
                      }}
                      placeholder="Código do cupom"
                    />
                    <Botao
                      id="btn_validar_cupom_reingresso"
                      type="button"
                      onClick={() => {
                        formCupomRef.current?.submitForm()
                      }}
                      texto="Validar"
                    />
                  </CupomContainer>
                </FormUnform>
              </ContainerInputCupom>
            ) : (
              <></>
            )}
          </ContainerCupom>
        </ContainerConteudo>
      )}

      <ContainerConteudo>
        <ContainerCadastro>
          <FormUnform
            ref={formRef}
            schema={schema}
            acaoSucesso={acaoSucesso}
            acaoFalha={focarPrimeiroCampoComErro}
          >
            {ehPagamentoRecorrente && (
              <TituloIsentoRecorrencia>
                ISENTO: Não há taxa a pagar por optar pelo pagamento recorrente.
              </TituloIsentoRecorrencia>
            )}

            {exibirCamposCartao ? (
              <>
                <h4>{tituloPagamento}</h4>
                <Linha>
                  <ContainerMedio>
                    {valorTotal > 0 && (
                      <LinhaPagamentoPix $inlinePix={ehMetodoPix}>
                        <ContainerMedio>
                          <SelectUnform
                            id="slc_metodo_pagamento"
                            name="metodoPagamento"
                            label="Selecione a Forma de Pagamento"
                            obrigatorio
                            opcoes={SELECT_METODOSPAGAMENTO}
                            valorAlterado={alterarMetodoPagamento}
                            disabled={!!boleto}
                          />
                        </ContainerMedio>
                        {ehMetodoPix && !boleto && (
                          <ContainerBotaoPix>
                            <Botao
                              texto="Gerar o Pix"
                              tema="Secundario"
                              carregando={carregandoBoleto}
                            />
                          </ContainerBotaoPix>
                        )}
                        {ehMetodoPix && boleto && (
                          <ContainerBotaoPix>
                            <BotaoStep
                              texto="Concluir"
                              tamanho={'S'}
                              textoNegrito="Matrícula"
                              type="button"
                              carregando={carregandoFinalizar}
                              disabled={!boleto && valorTotal > 0}
                              onClick={acaoFinalizar}
                              textoTooltip={
                                !boleto && valorTotal > 0
                                  ? 'É necessário realizar a geração do documento para prosseguir com o pagamento'
                                  : undefined
                              }
                            />
                          </ContainerBotaoPix>
                        )}
                      </LinhaPagamentoPix>
                    )}
                    {metodoPagamento === SELECT_METODOSPAGAMENTO[0].id && (
                      <>
                        <LinhaBandeiras>
                          <ContainerBandeiras
                            selecionado={
                              !!numeroCartao.match(regExpVisa) &&
                              !numeroCartao.match(regExpElo)
                            }
                          >
                            {IconeCartaoVisa}
                          </ContainerBandeiras>
                          <ContainerBandeiras
                            selecionado={!!numeroCartao.match(regExpMaster)}
                          >
                            {IconeCartaoMaster}
                          </ContainerBandeiras>
                          <ContainerBandeiras
                            selecionado={!!numeroCartao.match(regExpAmex)}
                          >
                            {IconeCartaoAmex}
                          </ContainerBandeiras>
                          <ContainerBandeiras
                            selecionado={!!numeroCartao.match(regExpElo)}
                          >
                            {IconeCartaoElo}
                          </ContainerBandeiras>
                          <ContainerBandeiras
                            selecionado={!!numeroCartao.match(regExpHiper)}
                          >
                            {IconeCartaoHiper}
                          </ContainerBandeiras>
                          <ContainerBandeiras
                            selecionado={!!numeroCartao.match(regExpJcb)}
                          >
                            {IconeCartaoJcb}
                          </ContainerBandeiras>
                          <ContainerBandeiras
                            selecionado={!!numeroCartao.match(regExpDiners)}
                          >
                            {IconeCartaoDiners}
                          </ContainerBandeiras>
                        </LinhaBandeiras>
                        <CartaoCreditoInputUnform
                          id="ipt_numero_cartao"
                          name="numeroCartao"
                          label="Número do Cartão"
                          obrigatorio
                          onChange={e => {
                            const valor = e.target.value.replaceAll(' ', '')
                            definirNumeroCartao(valor)
                          }}
                        />
                        <InputUnform
                          id="ipt_nome_cartao"
                          name="nomeCartao"
                          label="Nome Conforme Consta no Cartão"
                          obrigatorio
                        />
                        <Linha>
                          <ContainerMedio>
                            <InputMesAnoUnform
                              id="ipt_validade_cartao"
                              name="validadeCartao"
                              label="Validade do Cartão"
                              placeholder="mm/aaaa"
                              formatoAno="AAAA"
                              obrigatorio
                            />
                          </ContainerMedio>
                          <ContainerMedio>
                            <InputUnform
                              id="ipt_codigo_seguranca"
                              name="codigoSeguranca"
                              label={
                                <>
                                  Código de Segurança
                                  <IconeLabel
                                    data-tip="O código de segurança, ou CVV, refere-se ao código de 3 dígitos exibido na frente ou no verso do seu cartão."
                                    data-for="tooltip-cvv"
                                  >
                                    {IconeInformacao}
                                  </IconeLabel>
                                </>
                              }
                              obrigatorio
                              maxLength={4}
                            />
                            <Tooltip
                              id="tooltip-cvv"
                              place="right"
                              className="tooltip-ipgs"
                              textColor={Cores.PRETO}
                              backgroundColor={Cores.CINZA_1_CLARO}
                            />
                          </ContainerMedio>
                        </Linha>
                      </>
                    )}
                  </ContainerMedio>
                  {(metodoPagamento === SELECT_METODOSPAGAMENTO[0].id ||
                    metodoPagamento === SELECT_METODOSPAGAMENTO[1].id) && (
                  <ContainerMedio>
                    {!ehPagamentoRecorrente && (
                      <>
                        {metodoPagamento === SELECT_METODOSPAGAMENTO[0].id ? (
                          <SelectUnform
                            id="slc_parcelas"
                            name="parcelas"
                            label="Selecione a Quantidade de Parcelas"
                            obrigatorio
                            iconeFechar
                            opcoes={parcelasCartaoCredito}
                          />
                        ) : metodoPagamento === SELECT_METODOSPAGAMENTO[1].id ? (
                          <>
                            <Linha>
                              <ContainerMedio>
                                <SelectUnform
                                  id="slc_dia_vencimento"
                                  name="diaVencimento"
                                  label="Dia de Vencimento"
                                  placeholder="Selecione"
                                  obrigatorio
                                  opcoes={SELECT_DIAS_DIAS_BOLETO_AUTOMATICO}
                                  disabled
                                />
                              </ContainerMedio>
                              {!boleto && (
                                <ContainerBotaoBoleto>
                                  <Botao
                                    texto="Gerar o Boleto"
                                    tema="Secundario"
                                    carregando={carregandoBoleto}
                                  />
                                </ContainerBotaoBoleto>
                              )}
                              {boleto && (
                                <ContainerBotaoBoleto>
                                  <BotaoStep
                                    texto="Concluir"
                                    tamanho={'S'}
                                    textoNegrito="Matrícula"
                                    type="button"
                                    carregando={carregandoFinalizar}
                                    disabled={
                                      !boleto &&
                                      valorTotal > 0
                                    }
                                    onClick={acaoFinalizar}
                                    textoTooltip={
                                      !boleto &&
                                      valorTotal > 0
                                        ? 'É necessário realizar a geração do documento para prosseguir com o pagamento'
                                        : undefined
                                    }
                                  />
                                </ContainerBotaoBoleto>
                              )}
                            </Linha>
                          </>
                        ) : null}
                      </>
                    )}
                  </ContainerMedio>
                  )}
                </Linha>
              </>
            ) : (
              <TituloIsento>{textoIsencao}</TituloIsento>
            )}
            {renderizarBoleto()}
          </FormUnform>
          <ContainerAcoes>
            <BotaoStep
              invertido
              texto="Voltar para"
              textoNegrito="Termos e Condições"
              tema="Secundario"
              disabled={carregandoVoltar}
              onClick={() => {
                definirCarregandoVoltar(true)
                voltarWizard()
              }}
            />
            {!boleto && (
              <BotaoStep
                texto="Concluir"
                textoNegrito="Matrícula"
                type="button"
                carregando={carregandoFinalizar}
                disabled={
                  (metodoPagamento === SELECT_METODOSPAGAMENTO[1].id ||
                    metodoPagamento === SELECT_METODOSPAGAMENTO[2].id) &&
                  !boleto &&
                  valorTotal > 0
                }
                onClick={acaoFinalizar}
                textoTooltip={
                  (metodoPagamento === SELECT_METODOSPAGAMENTO[1].id ||
                    metodoPagamento === SELECT_METODOSPAGAMENTO[2].id) &&
                  !boleto &&
                  valorTotal > 0
                    ? 'É necessário realizar a geração do documento para prosseguir com o pagamento'
                    : undefined
                }
              />
            )}
          </ContainerAcoes>
        </ContainerCadastro>
      </ContainerConteudo>
      <ModalPagamentoReingresso ehBoleto={boleto} backdrop ref={modalRef} />
    </>
  )
}
